import type { Site } from '../../domain/inventory';

/**
 * Rebill freight is the sender's reference Cargonizer passes on to
 * X-Ledger: the project number Finance rebills the freight to, which is
 * the site code (e.g. "BDO"), or "Intern frakt" when the customer isn't
 * billed (Heidi, tidaltech/koi#8233). It is never filled in for the
 * person: a forgotten pre-filled project number bills the customer for
 * freight that should have been "Intern frakt".
 */
export const INTERNAL_FREIGHT = 'Intern frakt';

// Matches the backend's limit in checkSenderReference.
export const MAX_REBILL_REFERENCE_LENGTH = 100;

/**
 * "Intern frakt" first, then the chosen site's code, then the customer's
 * other site codes (every site's without a customer).
 */
export function rebillOptions(
  sites: readonly Site[],
  customerId?: string,
  siteId?: string,
): string[] {
  const chosen = sites.find((site) => site.id === siteId)?.name.trim();
  const codes = new Set(
    sites
      .filter((site) => !customerId || site.customerId === customerId)
      .map((site) => site.name.trim())
      .filter((code) => code && code !== chosen),
  );
  return [
    INTERNAL_FREIGHT,
    ...(chosen ? [chosen] : []),
    ...[...codes].sort((a, b) => a.localeCompare(b)),
  ];
}

/** Why the reference can't be sent, or null. */
export function rebillReferenceProblem(value: string): string | null {
  const ref = value.trim();
  if (!ref) return `Rebill freight is required: the project number, or “${INTERNAL_FREIGHT}”.`;
  if (ref.length > MAX_REBILL_REFERENCE_LENGTH) {
    return `Rebill freight must be at most ${MAX_REBILL_REFERENCE_LENGTH} characters.`;
  }
  return null;
}
